import { useEffect, useRef } from 'react';
import { Icon } from '@iconify/react';
import { useChatStore } from '@/store/useChatStore';
import { useTranslation } from 'react-i18next';
import { useStudioStore } from '@/store/useStudioStore.ts';
import { isSpecialProjectType } from '@/lib/projectUtils.ts';
import { getStudioBuildCopy } from './studioBuildCopy';

interface StudioBuildProgressProps {
  onDone?: (done: boolean) => void;
}

const ESTIMATED_SECONDS = 300;

export const StudioBuildProgress = ({ onDone }: StudioBuildProgressProps) => {
  const { t } = useTranslation('studio');
  const {
    isPreviewReady,
    elapsedSeconds,
    setElapsedSeconds,
    appendMessage,
    lastProgressIndex,
    setLastProgressIndex,
  } = useChatStore();
  const { currentProjectTypeId, isBuildDone, buildVariant } = useStudioStore();
  const buildCopy = getStudioBuildCopy(buildVariant);
  const progressSteps = buildCopy.steps;
  const lastEmittedIndexRef = useRef(lastProgressIndex);
  const showEstimatedTime = buildVariant !== 'clone';

  useEffect(() => {
    lastEmittedIndexRef.current = lastProgressIndex;
  }, [lastProgressIndex]);

  const isComplete = isPreviewReady || isBuildDone;
  const progress = isComplete ? 100 : Math.min((elapsedSeconds / ESTIMATED_SECONDS) * 99, 99);
  const isDone = isComplete && progress === 100;

  useEffect(() => {
    if (isSpecialProjectType(currentProjectTypeId)) return;

    const interval = ESTIMATED_SECONDS / (progressSteps.length - 1);
    const currentIndex = Math.floor(elapsedSeconds / interval);
    const targetIndex = Math.min(currentIndex, progressSteps.length - 2);

    if (targetIndex > lastEmittedIndexRef.current) {
      const prevIndex = lastEmittedIndexRef.current;
      lastEmittedIndexRef.current = targetIndex;

      for (let i = prevIndex + 1; i <= targetIndex; i += 1) {
        appendMessage({
          role: 'assistant',
          content: progressSteps[i],
        });
      }
      setLastProgressIndex(targetIndex);
    }
  }, [elapsedSeconds, currentProjectTypeId, appendMessage, progressSteps, setLastProgressIndex]);

  useEffect(() => {
    if (isDone && lastEmittedIndexRef.current < progressSteps.length - 1) {
      const prevIndex = lastEmittedIndexRef.current;
      lastEmittedIndexRef.current = progressSteps.length - 1;

      for (let i = prevIndex + 1; i < progressSteps.length; i += 1) {
        appendMessage({
          role: 'assistant',
          content: progressSteps[i],
        });
      }
      setLastProgressIndex(progressSteps.length - 1);
    }
  }, [appendMessage, isDone, progressSteps, setLastProgressIndex]);

  useEffect(() => {
    if (isDone) return;
    const timer = setInterval(() => {
      setElapsedSeconds((s) => s + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [isDone, setElapsedSeconds]);

  useEffect(() => {
    if (isDone) {
      onDone?.(true);
    }
  }, [isDone, onDone]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  if (isDone) return null;

  const stepInterval = ESTIMATED_SECONDS / (progressSteps.length - 1);
  const currentStepIndex = Math.min(
    Math.floor(elapsedSeconds / stepInterval),
    progressSteps.length - 1,
  );

  return (
    <div className="mb-[14px]">
      <div className="mb-2 flex flex-col gap-1 text-[12px] font-semibold text-[rgba(13,13,26,0.56)] sm:flex-row sm:items-center sm:justify-between sm:gap-2">
        <span className="flex-1 truncate">{progressSteps[currentStepIndex]}</span>
        <span className="shrink-0">{Math.floor(progress)}%</span>
      </div>
      <div className="mb-[14px] h-[10px] overflow-hidden rounded-full bg-[rgba(0,0,0,0.06)]">
        <div
          className="h-full bg-gradient-to-r from-[#2563EB] to-[#0EA5E9] transition-all duration-1000"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="flex flex-col gap-[10px] sm:flex-row sm:flex-wrap sm:items-center">
        <div className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[rgba(37,99,235,0.18)] bg-[rgba(37,99,235,0.07)] p-[9px_12px] text-[11.5px] font-semibold text-[#2563EB] shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] sm:w-36 sm:justify-start">
          <Icon icon="fa6-solid:clock" className="h-3 w-3 text-[#2563EB]" />
          {t('build.status.elapsed')}{' '}
          <strong className="text-[13px] font-bold">{formatTime(elapsedSeconds)}</strong>
        </div>
        {showEstimatedTime ? (
          <div className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[rgba(0,0,0,0.07)] bg-white p-[9px_12px] text-[11.5px] font-semibold text-[rgba(13,13,26,0.56)] shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] sm:w-auto sm:justify-start">
            {t('build.status.est')}
            <strong className="text-[13px] font-bold text-[#0D0D1A]">
              {Math.floor(ESTIMATED_SECONDS / 60)} {t('build.status.est_unit')}
            </strong>
          </div>
        ) : null}
        <p className="m-0 text-[12px] leading-[1.6] text-[rgba(13,13,26,0.56)] sm:flex-1 sm:basis-full sm:text-[12.5px]">
          {elapsedSeconds >= 900
            ? buildCopy.note15min
            : elapsedSeconds >= 600
              ? buildCopy.note10min
              : buildCopy.note}
        </p>
      </div>
    </div>
  );
};
